import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setPhaseRule } from "../../../../redux/Slices/smartAdSchedulerSlice";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { useGetAdCampaigns } from "../../../../queries/adMarketplace";

const buildPhaseLabels = (t) => ({
    pre_match:   t("smart_ads.phase_pre_match"),
    first_half:  t("smart_ads.phase_first_half"),
    halftime:    t("smart_ads.phase_halftime"),
    second_half: t("smart_ads.phase_second_half"),
    extra_time:  t("smart_ads.phase_extra_time"),
    fulltime:    t("smart_ads.phase_fulltime"),
});

const PHASE_ORDER = ["pre_match", "first_half", "halftime", "second_half", "extra_time", "fulltime"];

const AD_TYPE_ICONS = {
    video:          "pi pi-video",
    image:          "pi pi-image",
    ticker:         "pi pi-bars",
    silent_visual:  "pi pi-eye",
};

export default function PhaseRulesStep() {
    const { t }          = useTranslation();
    const dispatch       = useDispatch();
    const { phaseRules } = useSelector((s) => s.smartAdScheduler);

    const PHASE_LABELS = buildPhaseLabels(t);

    const { data: adCampaignsData } = useGetAdCampaigns({ status: "active", per_page: 100 });
    const adCampaigns = Array.isArray(adCampaignsData)
        ? adCampaignsData
        : adCampaignsData?.data ?? [];

    const campaignOptions = adCampaigns.map((c) => ({
        label: c.name,
        value: c.id,
        ad_type: c.ad_type,
    }));

    const campaignOptionTemplate = (option) => (
        <div className="flex items-center gap-2">
            <i className={`${AD_TYPE_ICONS[option.ad_type] ?? "pi pi-circle"} text-gray-400 text-xs`} />
            <span className="text-sm">{option.label}</span>
            <span className="ml-auto text-xs text-gray-400 capitalize">{option.ad_type?.replace("_", " ")}</span>
        </div>
    );

    const configuredCount = PHASE_ORDER.filter((p) => phaseRules[p]?.ad_campaign_id).length;

    return (
        <div>
            <p className="text-xs text-gray-500 mb-4">
                {t("smart_ads.phase_rules_hint")}
                <span className="ml-2 font-medium text-blue-600">{t("smart_ads.phase_configured_count", { count: configuredCount, total: PHASE_ORDER.length })}</span>
            </p>

            <div className="space-y-3">
                {PHASE_ORDER.map((phase) => {
                    const rule = phaseRules[phase];
                    const hasRule = !!rule?.ad_campaign_id;

                    return (
                        <div
                            key={phase}
                            className={`border rounded-lg p-3 transition-colors ${
                                hasRule ? "border-blue-200 bg-blue-50/40" : "border-gray-200"
                            }`}
                        >
                            <div className="flex items-center gap-3">
                                {/* Phase label */}
                                <div className="w-24 flex-shrink-0">
                                    <span className="text-xs font-semibold text-gray-700">{PHASE_LABELS[phase]}</span>
                                </div>

                                {/* Ad campaign selector */}
                                <div className="flex-1">
                                    <Dropdown
                                        value={rule?.ad_campaign_id ?? null}
                                        options={campaignOptions}
                                        onChange={(e) => dispatch(setPhaseRule({ phase, field: "ad_campaign_id", value: e.value }))}
                                        itemTemplate={campaignOptionTemplate}
                                        placeholder={t("smart_ads.no_ad_placeholder")}
                                        showClear
                                        className="w-full text-sm"
                                        panelClassName="text-sm"
                                    />
                                </div>

                                {/* Duration override */}
                                {hasRule && (
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        <InputNumber
                                            value={rule?.display_duration_seconds ?? null}
                                            onValueChange={(e) => dispatch(setPhaseRule({ phase, field: "display_duration_seconds", value: e.value }))}
                                            placeholder="s"
                                            min={1}
                                            max={3600}
                                            className="w-20 text-sm"
                                            inputClassName="text-xs"
                                            tooltip={t("smart_ads.duration_override_tooltip")}
                                            tooltipOptions={{ position: "top" }}
                                        />
                                        <span className="text-xs text-gray-400">s</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {configuredCount === 0 && (
                <p className="text-center text-xs text-amber-600 mt-3">
                    {t("smart_ads.phase_rules_required")}
                </p>
            )}
        </div>
    );
}
